// PlatformConsole — operator-only control plane for the SHARED multi-tenant
// Stage B engine. MANAGEMENT-ONLY: it lists customers and runs lifecycle
// transitions. It does NOT create customers — the Setup Wizard is the SOLE
// creation path (it drives POST /api/platform/tenants at its final step). This
// keeps one guarded, guided entry point for onboarding and one place for the
// collision/reactivate logic.
//
//   GET  /api/platform/tenants                 → list
//   POST /api/platform/tenants/:id/suspend      → status = suspended
//   POST /api/platform/tenants/:id/reactivate   → status = active
//   POST /api/platform/tenants/:id/offboard     → status = offboarded (SOFT, no delete)
//
// The operator gate is enforced SERVER-SIDE (routes/platformAdmin.js:operatorOnly,
// audited). This component is only mounted for is_operator users; that client-side
// hiding is a convenience, never the control. Editing an existing customer's
// settings (email, SSO, integrations, AI…) happens in that customer's OWN admin
// area, not here and not by re-running the wizard.

import { useState, useEffect, useCallback, useRef } from "react";
import { T } from "../theme";
import { fmtDate } from "../utils";
import { apiCall, apiCallEx } from "../apiClient";
import { Btn, Modal, Card, Badge, SectionTitle, EmptyState, RefreshBtn, TabBar } from "./primitives";
import { DefaultsEditor } from "./DefaultsEditor";
import { PlatformHealthPanel, TenantHealthPanel } from "./OperatorHealth";
import { BackupStatus } from "./admin/BackupStatus";
import { GlobalAiConsole } from "./admin/GlobalAiConsole";

const PLATFORM_TENANT_ID = "00000000-0000-0000-0000-000000000001";

interface TenantRow {
  tenant_id: string;
  name: string;
  status: string;
  edition: string;
  created_at: string;
  user_count: number | string;
}

const STATUS_STYLE: Record<string, { c: string; b: string; label: string }> = {
  active: { c: T.grn, b: T.grnS, label: "Active" },
  suspended: { c: T.ylw, b: T.ylwS, label: "Suspended" },
  offboarded: { c: T.txD, b: T.bg4, label: "Offboarded" },
};

export const PlatformConsole = ({ aiRoutingOnly = false }: { aiRoutingOnly?: boolean }) => {
  // aiRoutingOnly: the in-house / walls-down AI-routing operator (an
  // admin.manage_ai_routing holder). They get ONLY the Global AI panel — no
  // tenant/operator management (the backend also 403s those paths for them).
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Lifecycle confirm
  const [confirmOp, setConfirmOp] = useState<{ tenant: TenantRow; action: string; verb: string } | null>(null);
  const [acting, setActing] = useState(false);
  // Resend set-password invite (recovery fallback for a failed/lost auto-send)
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [notice, setNotice] = useState("");

  // Operator cross-customer user management (the deliberate wall-crossing surface)
  const [manageTenant, setManageTenant] = useState<TenantRow | null>(null);
  const [manageUsers, setManageUsers] = useState<any[]>([]);
  const [manageLoading, setManageLoading] = useState(false);
  const [manageErr, setManageErr] = useState("");
  const [manageMsg, setManageMsg] = useState("");
  const [rowBusy, setRowBusy] = useState<string | null>(null);

  // Owner directive 2026-07-21: the blocking reason-entry dialog is GONE.
  // Every action still lands in the append-only operator audit (who/what/
  // which tenant/when) — the backend records this system default as the
  // reason when none is supplied. promptReason keeps its signature so every
  // open-handler continues to work unchanged.
  const promptReason = (_action: string): string | null => "operator console action";
  const rq = (reason: string) => `reason=${encodeURIComponent(reason)}`;
  const [manageReason, setManageReason] = useState("");

  // ── WS-G user CRUD additions ───────────────────────────────────────────────
  const [addUser, setAddUser] = useState({ name: "", email: "", role: "requester" });
  const [addUserBusy, setAddUserBusy] = useState(false);
  const [delUser, setDelUser] = useState<any>(null);
  const [delUserText, setDelUserText] = useState("");
  const runAddUser = async () => {
    if (!manageTenant || !addUser.email.trim()) return;
    setAddUserBusy(true); setManageErr(""); setManageMsg("");
    const r = await apiCallEx(`/platform/tenants/${manageTenant.tenant_id}/users`, {
      method: "POST", body: JSON.stringify({ name: addUser.name.trim() || undefined, email: addUser.email.trim().toLowerCase(), role: addUser.role }),
    });
    setAddUserBusy(false);
    if (r.ok && r.data?.success) {
      setManageMsg(`${r.data.email} created${r.data.invited ? " and invited (set-password link sent)" : ", but the invite email failed — use Reset password"}.`);
      setAddUser({ name: "", email: "", role: "requester" });
      loadManage(manageTenant.tenant_id, manageReason || "operator console action");
    } else setManageErr(r.data?.message || r.data?.error || "Could not add the user.");
  };
  const runDeleteUser = async () => {
    if (!manageTenant || !delUser || delUserText !== delUser.email) return;
    setRowBusy(delUser.id + "delete"); setManageErr(""); setManageMsg("");
    const r = await apiCallEx(`/platform/tenants/${manageTenant.tenant_id}/users/${delUser.id}`, { method: "DELETE" });
    setRowBusy(null);
    if (r.ok && r.data?.success) {
      setManageMsg(`${delUser.email} permanently deleted.`);
      setDelUser(null); setDelUserText("");
      loadManage(manageTenant.tenant_id, manageReason || "operator console action");
    } else {
      setDelUser(null); setDelUserText("");
      setManageErr(r.data?.message || (r.data?.error === "last_admin" ? "Cannot delete the last active admin." : r.data?.error === "user_has_records" ? "This user owns records — deactivate instead." : "Could not delete the user."));
    }
  };

  // ── Provisioning Defaults: graphical editor (DefaultsEditor.tsx) ──────────
  const [defOpen, setDefOpen] = useState(false);

  // ── WS-F health views ──────────────────────────────────────────────────────
  const [healthOpen, setHealthOpen] = useState(false);
  const [healthTab, setHealthTab] = useState("health");
  const [healthTenant, setHealthTenant] = useState<TenantRow | null>(null);

  // ── WS-G permanent tenant deletion (type-the-name + second confirm) ────────
  const [delTenant, setDelTenant] = useState<TenantRow | null>(null);
  const [delName, setDelName] = useState("");
  const [delSecond, setDelSecond] = useState(false);
  const [delBusy, setDelBusy] = useState(false);
  const [delErr, setDelErr] = useState("");
  const runDeleteTenant = async () => {
    if (!delTenant || delName !== delTenant.name || !delSecond) return;
    setDelBusy(true); setDelErr("");
    const r = await apiCallEx(`/platform/tenants/${delTenant.tenant_id}/delete-permanently`, {
      method: "POST", body: JSON.stringify({ confirm_name: delName }),
    });
    setDelBusy(false);
    if (r.ok && r.data?.success) {
      setDelTenant(null); setDelName(""); setDelSecond(false);
      setNotice(`Tenant "${r.data.name}" permanently deleted (${Object.keys(r.data.report || {}).filter(k => !k.startsWith("_")).length} tables cleaned).`);
      load();
    } else {
      setDelErr(r.data?.message || r.data?.error || "Deletion failed — nothing was removed (all-or-nothing).");
    }
  };

  const [opsOpen, setOpsOpen] = useState(false);
  const [operators, setOperators] = useState<any[]>([]);
  const [opsLoading, setOpsLoading] = useState(false);
  const [opsErr, setOpsErr] = useState("");
  const [opsMsg, setOpsMsg] = useState("");
  const [opAddEmail, setOpAddEmail] = useState("");
  const [opAddName, setOpAddName] = useState("");
  const [opBusy, setOpBusy] = useState(false);

  const loadOperators = useCallback(async () => {
    setOpsLoading(true); setOpsErr("");
    const res = await apiCallEx('/platform/operators');
    setOpsLoading(false);
    if (res.ok && Array.isArray(res.data?.operators)) setOperators(res.data.operators);
    else { setOperators([]); setOpsErr("Could not load operators."); }
  }, []);
  const openOperators = () => { setOpsOpen(true); setOpsMsg(""); setOpsErr(""); setOpAddEmail(""); setOpAddName(""); loadOperators(); };
  const addOperator = async () => {
    const email = opAddEmail.trim().toLowerCase();
    if (!email) { setOpsErr("Enter an email."); return; }
    const reason = promptReason(`add operator ${email}`);
    if (!reason) return;
    setOpBusy(true); setOpsErr(""); setOpsMsg("");
    const res = await apiCallEx('/platform/operators', { method: 'POST', body: JSON.stringify({ email, name: opAddName.trim(), reason }) });
    setOpBusy(false);
    if (res.ok) {
      setOpsMsg(`${res.data.promoted ? 'Promoted' : 'Added'} ${email}${res.data.invite_sent ? ' — set-password invite sent.' : ' — invite NOT sent (stage email creds).'}`);
      setOpAddEmail(""); setOpAddName(""); loadOperators();
    } else setOpsErr(res.data?.message || res.data?.error || 'Failed to add operator.');
  };
  const removeOperator = async (op: any) => {
    const reason = promptReason(`remove operator ${op.email}`);
    if (!reason) return;
    setOpBusy(true); setOpsErr(""); setOpsMsg("");
    const res = await apiCallEx(`/platform/operators/${op.id}?${rq(reason)}`, { method: 'DELETE' });
    setOpBusy(false);
    if (res.ok) { setOpsMsg(`Removed ${op.email}.`); loadOperators(); }
    else setOpsErr(res.data?.message || res.data?.error || 'Failed to remove operator.');
  };

  const loadManage = useCallback(async (tenantId: string, reason: string) => {
    setManageLoading(true); setManageErr(""); setManageMsg("");
    const res = await apiCallEx(`/platform/tenants/${tenantId}/users?reason=${encodeURIComponent(reason)}`);
    setManageLoading(false);
    if (res.ok && Array.isArray(res.data?.users)) setManageUsers(res.data.users);
    else { setManageUsers([]); setManageErr("Could not load this customer's users."); }
  }, []);

  const openManage = (t: TenantRow) => {
    const reason = promptReason(`view & manage users of "${t.name}"`);
    if (!reason) return;
    setManageReason(reason); setManageTenant(t); setManageUsers([]); setManageErr(""); setManageMsg(""); loadManage(t.tenant_id, reason);
  };

  // Operator BYO AI key (support path) — write-only, validated on save.
  // Multi-provider BYO AI keys (WS-G 2026-07-21): Anthropic + OpenAI + Groq.
  // Each key is validated live with its provider on save, stored encrypted,
  // never shown again; removal falls back to the KontractAI default key.
  const BYO_AI_PROVIDERS: { id: string; label: string }[] = [
    { id: "anthropic", label: "Anthropic" },
    { id: "openai", label: "OpenAI" },
    { id: "groq", label: "Groq" },
  ];
  const [aiKeyTenant, setAiKeyTenant] = useState<TenantRow | null>(null);
  const [aiKeyProviders, setAiKeyProviders] = useState<Record<string, boolean>>({});
  const [aiKeyInput, setAiKeyInput] = useState<Record<string, string>>({});
  const [aiKeyBusy, setAiKeyBusy] = useState<string | null>(null);
  const [aiKeyErr, setAiKeyErr] = useState(""); const [aiKeyMsg, setAiKeyMsg] = useState("");
  const openAiKey = async (t: TenantRow) => {
    setAiKeyTenant(t); setAiKeyInput({}); setAiKeyErr(""); setAiKeyMsg(""); setAiKeyProviders({});
    const r = await apiCallEx(`/platform/tenants/${t.tenant_id}/ai-key`);
    if (r.ok && r.data?.providers) setAiKeyProviders(r.data.providers);
  };
  const saveAiKey = async (provider: string) => {
    const key = (aiKeyInput[provider] || "").trim();
    if (!aiKeyTenant || !key) return;
    setAiKeyBusy(provider); setAiKeyErr(""); setAiKeyMsg("");
    const r = await apiCallEx(`/platform/tenants/${aiKeyTenant.tenant_id}/ai-key`, { method: "PUT", body: JSON.stringify({ provider, api_key: key }) });
    setAiKeyBusy(null);
    if (r.ok && r.data?.success) { setAiKeyInput(p => ({ ...p, [provider]: "" })); setAiKeyProviders(p => ({ ...p, [provider]: true })); setAiKeyMsg(`${provider} key validated and saved for this customer.`); }
    else setAiKeyErr(r.data?.message || (r.data?.error === "invalid_key" ? `That ${provider} key could not be validated with the provider and was not saved.` : "Could not save the key."));
  };
  const clearAiKey = async (provider: string) => {
    if (!aiKeyTenant) return;
    setAiKeyBusy(provider); setAiKeyErr(""); setAiKeyMsg("");
    const r = await apiCallEx(`/platform/tenants/${aiKeyTenant.tenant_id}/ai-key?provider=${provider}`, { method: "DELETE" });
    setAiKeyBusy(null);
    if (r.ok) { setAiKeyProviders(p => ({ ...p, [provider]: false })); setAiKeyMsg(`${provider} key removed — this customer falls back to the KontractAI default.`); } else setAiKeyErr("Could not remove the key.");
  };

  // Operator per-tenant AI routing — Tier 1/2/3 temperature overrides. Models stay
  // global (governed by the catalog); only temperature is per-tenant here. The
  // temperature input is disabled with a note when the tier's model rejects it.
  interface RoutingTier {
    tier: string; model: string | null; provider: string | null;
    global_temperature: number | null; supports_temperature: boolean;
    override_temperature: number | null; effective_temperature: number | null; effective_source: string;
  }
  const [routingTenant, setRoutingTenant] = useState<TenantRow | null>(null);
  const [routingTiers, setRoutingTiers] = useState<RoutingTier[]>([]);
  const [routingInput, setRoutingInput] = useState<Record<string, string>>({});
  const [routingReason, setRoutingReason] = useState("");
  const [routingBusy, setRoutingBusy] = useState<string | null>(null);
  const [routingErr, setRoutingErr] = useState(""); const [routingMsg, setRoutingMsg] = useState("");
  const loadRouting = async (tenantId: string, reason: string) => {
    const r = await apiCallEx(`/platform/tenants/${tenantId}/ai-routing?${rq(reason)}`);
    if (r.ok && Array.isArray(r.data?.tiers)) {
      setRoutingTiers(r.data.tiers);
      const init: Record<string, string> = {};
      for (const t of r.data.tiers as RoutingTier[]) init[t.tier] = t.override_temperature != null ? String(t.override_temperature) : "";
      setRoutingInput(init);
    } else setRoutingErr("Could not load this customer's AI routing.");
  };
  const openRouting = async (t: TenantRow) => {
    const reason = promptReason(`view/adjust AI routing temperature for "${t.name}"`);
    if (!reason) return;
    setRoutingReason(reason); setRoutingTenant(t); setRoutingTiers([]); setRoutingInput({}); setRoutingErr(""); setRoutingMsg("");
    loadRouting(t.tenant_id, reason);
  };
  const saveRoutingTier = async (tier: string) => {
    if (!routingTenant) return;
    const raw = (routingInput[tier] || "").trim();
    if (raw === "") { setRoutingErr("Enter a temperature (0–1), or use Reset to inherit the global value."); return; }
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0 || n > 1) { setRoutingErr("Temperature must be a number between 0 and 1."); return; }
    setRoutingBusy(tier + "save"); setRoutingErr(""); setRoutingMsg("");
    const r = await apiCallEx(`/platform/tenants/${routingTenant.tenant_id}/ai-routing/${tier}`, { method: "PUT", body: JSON.stringify({ temperature: n, reason: routingReason }) });
    setRoutingBusy(null);
    if (r.ok && r.data?.success) { setRoutingMsg(`Tier ${tier} temperature set to ${r.data.temperature} for this customer.`); loadRouting(routingTenant.tenant_id, routingReason); }
    else setRoutingErr(r.data?.message || r.data?.error || "Could not save the override.");
  };
  const resetRoutingTier = async (tier: string) => {
    if (!routingTenant) return;
    setRoutingBusy(tier + "reset"); setRoutingErr(""); setRoutingMsg("");
    const r = await apiCallEx(`/platform/tenants/${routingTenant.tenant_id}/ai-routing/${tier}?${rq(routingReason)}`, { method: "DELETE" });
    setRoutingBusy(null);
    if (r.ok && r.data?.success) { setRoutingMsg(`Tier ${tier} reset — this customer inherits the global temperature.`); loadRouting(routingTenant.tenant_id, routingReason); }
    else setRoutingErr(r.data?.message || "Could not reset the override.");
  };

  const userAction = async (uid: string, action: "deactivate" | "reactivate" | "reset-invite", label: string) => {
    if (!manageTenant) return;
    setRowBusy(uid + action); setManageErr(""); setManageMsg("");
    const res = await apiCallEx(`/platform/tenants/${manageTenant.tenant_id}/users/${uid}/${action}`, { method: "POST", body: JSON.stringify({ reason: manageReason }) });
    setRowBusy(null);
    if (res.ok && res.data?.success) { setManageMsg(label); loadManage(manageTenant.tenant_id, manageReason); }
    else if (res.data?.error === "last_admin") setManageErr(res.data.message || "Cannot deactivate the last active admin.");
    else if (res.status === 502) setManageErr(`Invite email could not be sent to ${res.data?.email || "the user"} — retry.`);
    else setManageErr(res.data?.message || res.data?.error || "Action failed. Please retry.");
  };

  // Operator Microsoft Graph email credential staging — reusable credential pattern
  // (SharePoint/SSO will reuse the same /credentials/:type routes). Secret masked.
  const EMAIL_FIELD_ORDER = ["KONTRACTAI_EMAIL_TENANT_ID", "KONTRACTAI_EMAIL_CLIENT_ID", "KONTRACTAI_EMAIL_CLIENT_SECRET", "KONTRACTAI_EMAIL_FROM", "KONTRACTAI_EMAIL_INBOUND_MAILBOX", "KONTRACTAI_EMAIL_ADDITIONAL_INBOUND"];
  const [emailTenant, setEmailTenant] = useState<TenantRow | null>(null);
  const [emailFields, setEmailFields] = useState<Record<string, any> | null>(null);
  const [emailInput, setEmailInput] = useState<Record<string, string>>({});
  const [emailReason, setEmailReason] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailErr, setEmailErr] = useState(""); const [emailMsg, setEmailMsg] = useState("");
  const loadEmail = async (tenantId: string, reason: string) => {
    const r = await apiCallEx(`/platform/tenants/${tenantId}/credentials/graph-email?${rq(reason)}`);
    if (r.ok) setEmailFields(r.data?.fields || null); else setEmailErr("Could not load the email configuration.");
  };
  const openEmail = async (t: TenantRow) => {
    const reason = promptReason(`view/stage Microsoft Graph email for "${t.name}"`);
    if (!reason) return;
    setEmailReason(reason); setEmailTenant(t); setEmailInput({}); setEmailErr(""); setEmailMsg(""); setEmailFields(null);
    loadEmail(t.tenant_id, reason);
  };
  const saveEmail = async () => {
    if (!emailTenant) return;
    const values: Record<string, string> = {};
    for (const k of EMAIL_FIELD_ORDER) if ((emailInput[k] || "").trim()) values[k] = emailInput[k].trim();
    if (!Object.keys(values).length) { setEmailErr("Enter at least one field to stage."); return; }
    setEmailBusy(true); setEmailErr(""); setEmailMsg("");
    const r = await apiCallEx(`/platform/tenants/${emailTenant.tenant_id}/credentials/graph-email`, { method: "PUT", body: JSON.stringify({ values, reason: emailReason }) });
    setEmailBusy(false);
    if (r.ok && r.data?.success) { setEmailInput({}); setEmailMsg("Microsoft Graph email credentials staged (encrypted at rest)."); loadEmail(emailTenant.tenant_id, emailReason); }
    else setEmailErr(r.data?.message || "Could not stage the email credentials.");
  };
  const clearEmail = async () => {
    if (!emailTenant) return;
    setEmailBusy(true); setEmailErr(""); setEmailMsg("");
    const r = await apiCallEx(`/platform/tenants/${emailTenant.tenant_id}/credentials/graph-email?${rq(emailReason)}`, { method: "DELETE" });
    setEmailBusy(false);
    if (r.ok) { setEmailFields(null); setEmailMsg("Email credentials cleared — this customer falls back to the KontractAI default."); } else setEmailErr("Could not clear the email credentials.");
  };

  // Operator-staged Harvey API key — same schema-driven /credentials/:type pattern as
  // graph-email; single secret field byo_api_key_harvey, masked/write-only.
  const HARVEY_KEY_FIELD = "byo_api_key_harvey";
  const [harveyTenant, setHarveyTenant] = useState<TenantRow | null>(null);
  const [harveyFields, setHarveyFields] = useState<Record<string, any> | null>(null);
  const [harveyInput, setHarveyInput] = useState("");
  const [harveyReason, setHarveyReason] = useState("");
  const [harveyBusy, setHarveyBusy] = useState(false);
  const [harveyErr, setHarveyErr] = useState(""); const [harveyMsg, setHarveyMsg] = useState("");
  const loadHarvey = async (tenantId: string, reason: string) => {
    const r = await apiCallEx(`/platform/tenants/${tenantId}/credentials/harvey?${rq(reason)}`);
    if (r.ok) setHarveyFields(r.data?.fields || null); else setHarveyErr("Could not load the Harvey configuration.");
  };
  const openHarvey = async (t: TenantRow) => {
    const reason = promptReason(`view/stage the Harvey API key for "${t.name}"`);
    if (!reason) return;
    setHarveyReason(reason); setHarveyTenant(t); setHarveyInput(""); setHarveyErr(""); setHarveyMsg(""); setHarveyFields(null);
    loadHarvey(t.tenant_id, reason);
  };
  const saveHarvey = async () => {
    if (!harveyTenant || !harveyInput.trim()) { setHarveyErr("Enter a Harvey API key to stage."); return; }
    setHarveyBusy(true); setHarveyErr(""); setHarveyMsg("");
    const r = await apiCallEx(`/platform/tenants/${harveyTenant.tenant_id}/credentials/harvey`, { method: "PUT", body: JSON.stringify({ values: { [HARVEY_KEY_FIELD]: harveyInput.trim() }, reason: harveyReason }) });
    setHarveyBusy(false);
    if (r.ok && r.data?.success) { setHarveyInput(""); setHarveyMsg("Harvey API key staged (encrypted at rest)."); loadHarvey(harveyTenant.tenant_id, harveyReason); }
    else setHarveyErr(r.data?.message || "Could not stage the Harvey key.");
  };
  const clearHarvey = async () => {
    if (!harveyTenant) return;
    setHarveyBusy(true); setHarveyErr(""); setHarveyMsg("");
    const r = await apiCallEx(`/platform/tenants/${harveyTenant.tenant_id}/credentials/harvey?${rq(harveyReason)}`, { method: "DELETE" });
    setHarveyBusy(false);
    if (r.ok) { setHarveyFields(null); setHarveyMsg("Harvey key cleared — this customer falls back to the KontractAI default."); } else setHarveyErr("Could not clear the Harvey key.");
  };

  // Operator-staged DocuSign credentials — same schema-driven /credentials/:type
  // pattern (validated + stored by the SHARED docusignConfigService, identical to
  // the tenant settings door). Staging reaches "Configured — not connected"; the
  // one-time OAuth consent still needs a human with the designated DocuSign
  // user's login. Read-only tri-state + health from /esign-status.
  const DS_FIELD_ORDER = ["docusign_integration_key", "docusign_secret_key", "docusign_account_id", "docusign_base_url", "docusign_auth_url", "docusign_redirect_uri", "docusign_connect_hmac_key"];
  // Integration key included since the 2026-07-21 readback closure: the view
  // endpoint no longer returns its value (configured badge only), so the input
  // is write-only ("enter to replace") like the other secrets.
  const DS_SECRET_FIELDS = new Set(["docusign_secret_key", "docusign_connect_hmac_key", "docusign_integration_key"]);
  const [dsTenant, setDsTenant] = useState<TenantRow | null>(null);
  const [dsFields, setDsFields] = useState<Record<string, any> | null>(null);
  const [dsStatus, setDsStatus] = useState<Record<string, any> | null>(null);
  const [dsInput, setDsInput] = useState<Record<string, string>>({});
  const [dsReason, setDsReason] = useState("");
  const [dsBusy, setDsBusy] = useState(false);
  const [dsErr, setDsErr] = useState(""); const [dsMsg, setDsMsg] = useState("");
  const loadDocusign = async (tenantId: string, reason: string) => {
    const r = await apiCallEx(`/platform/tenants/${tenantId}/credentials/docusign?${rq(reason)}`);
    if (r.ok) setDsFields(r.data?.fields || null); else setDsErr("Could not load the DocuSign configuration.");
    const s = await apiCallEx(`/platform/tenants/${tenantId}/esign-status?${rq(reason)}`);
    if (s.ok) setDsStatus(s.data || null);
  };
  const openDocusign = async (t: TenantRow) => {
    const reason = promptReason(`view/stage DocuSign credentials for "${t.name}"`);
    if (!reason) return;
    setDsReason(reason); setDsTenant(t); setDsInput({}); setDsErr(""); setDsMsg(""); setDsFields(null); setDsStatus(null);
    loadDocusign(t.tenant_id, reason);
  };
  const saveDocusign = async () => {
    if (!dsTenant) return;
    const values: Record<string, string> = {};
    for (const k of DS_FIELD_ORDER) if ((dsInput[k] || "").trim()) values[k] = dsInput[k].trim();
    if (!Object.keys(values).length) { setDsErr("Enter at least one field to stage."); return; }
    setDsBusy(true); setDsErr(""); setDsMsg("");
    const r = await apiCallEx(`/platform/tenants/${dsTenant.tenant_id}/credentials/docusign`, { method: "PUT", body: JSON.stringify({ values, reason: dsReason }) });
    setDsBusy(false);
    if (r.ok && r.data?.success) { setDsInput({}); setDsMsg("DocuSign credentials staged (secrets encrypted at rest). The one-time DocuSign consent still needs to be completed to connect."); loadDocusign(dsTenant.tenant_id, dsReason); }
    else setDsErr(r.data?.message || "Could not stage the DocuSign credentials.");
  };
  const clearDocusign = async () => {
    if (!dsTenant) return;
    setDsBusy(true); setDsErr(""); setDsMsg("");
    const r = await apiCallEx(`/platform/tenants/${dsTenant.tenant_id}/credentials/docusign?${rq(dsReason)}`, { method: "DELETE" });
    setDsBusy(false);
    if (r.ok) { setDsFields(null); setDsMsg("DocuSign credentials cleared (connection tokens and health state removed)."); loadDocusign(dsTenant.tenant_id, dsReason); } else setDsErr("Could not clear the DocuSign credentials.");
  };
  const dsStatusColor = (s: string | undefined) => s === "connected" ? T.grn : s === "configured_not_connected" ? T.ylw : T.red;
  const dsStatusBg = (s: string | undefined) => s === "connected" ? T.grnS : s === "configured_not_connected" ? T.ylwS : T.redS;

  // ── Global AI (operator) — routing catalog + platform default keys ──
  // The console body now lives in the shared <GlobalAiConsole> component
  // (frontend/src/components/admin/GlobalAiConsole.tsx) so this modal and the
  // consolidated Admin ▸ AI & Models ▸ AI Routing screen render ONE implementation.
  // PlatformConsole only owns the modal open/close and a dirty flag the component
  // reports (via onDirtyChange) so we can still guard a discard on close.
  const [globalOpen, setGlobalOpen] = useState(false);
  const [globalDirty, setGlobalDirty] = useState(false);
  const openGlobal = () => { setGlobalDirty(false); setGlobalOpen(true); };
  // Closing with unsaved provider/model picks warns before discarding (no auto-save).
  const closeGlobal = () => {
    if (globalDirty && !window.confirm("You have unsaved provider/model changes on the routing catalog. Discard them?")) return;
    setGlobalOpen(false);
  };

  const load = useCallback(async () => {
    setLoading(true);
    const data = await apiCall("/platform/tenants");
    if (data && Array.isArray(data.tenants)) {
      setTenants(data.tenants);
      setErr("");
    } else {
      setErr("Could not load tenants. You may not have operator access.");
    }
    setLoading(false);
  }, []);

  useEffect(() => { if (!aiRoutingOnly) load(); }, [load, aiRoutingOnly]);
  // In-house AI-routing operator: skip the tenant load (that path 403s for them)
  // and land straight on the Global AI panel. openGlobal is intentionally omitted
  // from deps (it is not memoized); the effect should fire only when aiRoutingOnly
  // becomes true (i.e. on mount for such a user).
  const didAutoOpenGlobal = useRef(false);
  useEffect(() => {
    if (aiRoutingOnly && !didAutoOpenGlobal.current) { didAutoOpenGlobal.current = true; openGlobal(); }
  }, [aiRoutingOnly]);

  const runLifecycle = async () => {
    if (!confirmOp) return;
    setActing(true);
    const res = await apiCall(`/platform/tenants/${confirmOp.tenant.tenant_id}/${confirmOp.action}`, { method: "POST" });
    setActing(false);
    setConfirmOp(null);
    if (!res) { setErr("Action failed. Please retry."); return; }
    load();
  };

  // Recovery fallback: re-send the admin's single-use set-password invite (no
  // password is ever shown; the email always comes from the KontractAI mailbox).
  const resendInvite = async (t: TenantRow) => {
    setResendingId(t.tenant_id); setErr(""); setNotice("");
    const res = await apiCallEx(`/platform/tenants/${t.tenant_id}/resend-invite`, { method: "POST" });
    setResendingId(null);
    if (res.ok && res.data?.ok) { setNotice(`Set-password invite re-sent to ${res.data.admin_email} from KontractAI.`); return; }
    setErr(res.data?.error === "no_admin_found" ? "No admin found for this customer." : "Could not resend the invite. Please retry.");
  };

  const th: React.CSSProperties = { textAlign: "left", padding: "10px 14px", fontSize: 11, fontWeight: 700, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", borderBottom: `1px solid ${T.brd}` };
  const td: React.CSSProperties = { padding: "12px 14px", fontSize: 14, color: T.tx, borderBottom: `1px solid ${T.brdSubtle}`, verticalAlign: "middle" };

  // Operator SSO (Microsoft Entra OIDC) — console-level config of the SINGLE
  // bootstrap-mapped provider row. The safe shape (platform tenant, auto-provision
  // OFF, non-admin default role) is hard-enforced server-side; this is the UI.
  const inpStyle: React.CSSProperties = { padding: "7px 10px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 13 };
  const [ssoOpen, setSsoOpen] = useState(false);
  const [ssoConfig, setSsoConfig] = useState<any>(null);
  // No default email domain: the removed "kontractai.com" is the owner's own
  // domain, and prefilling it meant an operator could activate a tenant's
  // sign-in against the owner's domain by simply not noticing the field.
  const [ssoInput, setSsoInput] = useState({ client_id: "", client_secret: "", tenant_id_external: "", allowed_email_domains: "", is_active: false });
  const [ssoReason, setSsoReason] = useState("");
  const [ssoBusy, setSsoBusy] = useState(false);
  const [ssoErr, setSsoErr] = useState(""); const [ssoMsg, setSsoMsg] = useState("");
  const loadSso = async (reason: string) => {
    const r = await apiCallEx(`/platform/operator-sso?${rq(reason)}`);
    if (r.ok) {
      const c = r.data?.config || null; setSsoConfig(c);
      if (c) setSsoInput((s) => ({ ...s, client_id: c.client_id || "", tenant_id_external: c.tenant_id_external || "", allowed_email_domains: (c.allowed_email_domains || []).join(", "), is_active: !!c.is_active }));
    } else setSsoErr("Could not load the operator SSO configuration.");
  };
  const openSso = async () => {
    const reason = promptReason("view/configure operator SSO (Microsoft Entra)");
    if (!reason) return;
    setSsoReason(reason); setSsoOpen(true); setSsoErr(""); setSsoMsg(""); setSsoConfig(null);
    setSsoInput({ client_id: "", client_secret: "", tenant_id_external: "", allowed_email_domains: "", is_active: false });
    await loadSso(reason);
  };
  const saveSso = async () => {
    setSsoBusy(true); setSsoErr(""); setSsoMsg("");
    const domains = ssoInput.allowed_email_domains.split(",").map((d) => d.trim().toLowerCase()).filter(Boolean);
    const body: any = { client_id: ssoInput.client_id.trim(), tenant_id_external: ssoInput.tenant_id_external.trim(), allowed_email_domains: domains, is_active: ssoInput.is_active, reason: ssoReason };
    if (ssoInput.client_secret.trim()) body.client_secret = ssoInput.client_secret.trim();
    const r = await apiCallEx(`/platform/operator-sso`, { method: "PUT", body: JSON.stringify(body) });
    setSsoBusy(false);
    if (r.ok && r.data?.success) { setSsoInput((s) => ({ ...s, client_secret: "" })); setSsoMsg("Operator SSO saved — default-deny (auto-provision off); secret encrypted at rest."); loadSso(ssoReason); }
    else setSsoErr(r.data?.message || "Could not save operator SSO.");
  };
  const deactivateSso = async () => {
    setSsoBusy(true); setSsoErr(""); setSsoMsg("");
    const r = await apiCallEx(`/platform/operator-sso?${rq(ssoReason)}`, { method: "DELETE" });
    setSsoBusy(false);
    if (r.ok && r.data?.success) { setSsoMsg("Operator SSO deactivated (is_active=false)."); loadSso(ssoReason); }
    else setSsoErr("Could not deactivate operator SSO.");
  };

  return (
    <div style={{ padding: 32, maxWidth: 1100, margin: "0 auto" }}>
      <SectionTitle
        sub={aiRoutingOnly
          ? "AI routing catalog + platform default keys for this deployment."
          : "Operator-only · manage customers on the shared multi-tenant engine. Create a customer in the Setup Wizard."}
        right={aiRoutingOnly
          ? <div style={{ display: "flex", gap: 8 }}><Btn v="secondary" sz="sm" onClick={openGlobal}>Global AI</Btn></div>
          : <div style={{ display: "flex", gap: 8 }}><Btn v="secondary" sz="sm" onClick={() => setDefOpen(true)}>Provisioning Defaults</Btn><Btn v="secondary" sz="sm" onClick={() => setHealthOpen(true)}>Platform Health</Btn><Btn v="secondary" sz="sm" onClick={openGlobal}>Global AI</Btn><Btn v="secondary" sz="sm" onClick={openOperators}>Operators</Btn><Btn v="secondary" sz="sm" onClick={openSso}>Operator SSO</Btn><RefreshBtn onClick={load} /></div>}
      >
        {aiRoutingOnly ? "AI Routing" : "Platform Console"}
      </SectionTitle>

      {err && (
        <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 6, backgroundColor: T.redS, color: T.red, fontSize: 13, border: `1px solid ${T.red}` }}>
          {err}
        </div>
      )}
      {notice && (
        <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 6, backgroundColor: T.grnS, color: T.grn, fontSize: 13, border: `1px solid ${T.grn}` }}>
          {notice}
        </div>
      )}

      {aiRoutingOnly ? (
        <Card style={{ textAlign: "center", color: T.txM, fontSize: 14 }}>Open <strong>Global AI</strong> to manage providers, the model catalog, tiers, and platform default keys.</Card>
      ) : loading ? (
        <Card style={{ textAlign: "center", color: T.txM, fontSize: 14 }}>Loading tenants…</Card>
      ) : tenants.length === 0 ? (
        <EmptyState icon="◇" msg="No customers yet. Use the Setup Wizard to onboard the first one." />
      ) : (
        <Card style={{ padding: 0, overflowX: "auto" }}>
          {/* overflowX auto (was hidden): with the full action set the row is
              wider than the card — hidden CLIPPED the right-most buttons. */}
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Organization</th>
                <th style={th}>Status</th>
                <th style={th}>Edition</th>
                <th style={th}>Users</th>
                <th style={th}>Created</th>
                <th style={{ ...th, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map(t => {
                const isPlatform = String(t.tenant_id) === PLATFORM_TENANT_ID;
                const st = STATUS_STYLE[t.status] || { c: T.txM, b: T.bg4, label: t.status };
                return (
                  <tr key={t.tenant_id}>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{t.name}</div>
                      {isPlatform && <div style={{ fontSize: 11, color: T.txD, marginTop: 2 }}>Platform tenant</div>}
                    </td>
                    <td style={td}><Badge c={st.c} b={st.b}>{st.label}</Badge></td>
                    <td style={{ ...td, textTransform: "capitalize" }}>{(t.edition || "").replace("in_house", "In-House")}</td>
                    <td style={td}>{t.user_count}</td>
                    <td style={{ ...td, color: T.txM }}>{t.created_at ? fmtDate(t.created_at) : "—"}</td>
                    <td style={{ ...td, textAlign: "right" }}>
                      {isPlatform ? (
                        <span style={{ fontSize: 12, color: T.txD }}>—</span>
                      ) : (
                        <div style={{ display: "inline-flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end", maxWidth: 460 }}>
                          {t.status === "active" && (
                            <Btn v="secondary" sz="sm" onClick={() => openManage(t)}>Manage users</Btn>
                          )}
                          {t.status === "active" && (
                            <Btn v="ghost" sz="sm" onClick={() => openAiKey(t)}>AI key</Btn>
                          )}
                          {t.status === "active" && (
                            <Btn v="ghost" sz="sm" onClick={() => openRouting(t)}>AI routing</Btn>
                          )}
                          {t.status === "active" && (
                            <Btn v="ghost" sz="sm" onClick={() => openEmail(t)}>Email</Btn>
                          )}
                          {t.status === "active" && (
                            <Btn v="ghost" sz="sm" onClick={() => openHarvey(t)}>Harvey</Btn>
                          )}
                          {t.status === "active" && (
                            <Btn v="ghost" sz="sm" onClick={() => openDocusign(t)}>DocuSign</Btn>
                          )}
                          {t.status === "active" && (
                            <Btn v="ghost" sz="sm" onClick={() => resendInvite(t)} disabled={resendingId === t.tenant_id}>{resendingId === t.tenant_id ? "Sending…" : "Resend invite"}</Btn>
                          )}
                          {t.status === "active" && (
                            <Btn v="secondary" sz="sm" onClick={() => setConfirmOp({ tenant: t, action: "suspend", verb: "Suspend" })}>Suspend</Btn>
                          )}
                          {(t.status === "suspended" || t.status === "offboarded") && (
                            <Btn v="success" sz="sm" onClick={() => setConfirmOp({ tenant: t, action: "reactivate", verb: "Reactivate" })}>Reactivate</Btn>
                          )}
                          {t.status !== "offboarded" && (
                            <Btn v="danger" sz="sm" onClick={() => setConfirmOp({ tenant: t, action: "offboard", verb: "Offboard" })}>Offboard</Btn>
                          )}
                          <Btn v="ghost" sz="sm" onClick={() => setHealthTenant(t)}>Health</Btn>
                          <Btn v="danger" sz="sm" onClick={() => { setDelTenant(t); setDelName(""); setDelSecond(false); setDelErr(""); }}>Delete…</Btn>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {/* ─── Operator per-tenant BYO AI key (support) ─── */}
      <Modal open={!!aiKeyTenant} onClose={() => setAiKeyTenant(null)} title={aiKeyTenant ? `AI key · ${aiKeyTenant.name}` : ""} w={520}>
        {aiKeyTenant && (
          <div>
            <p style={{ fontSize: 12, color: T.txM, margin: "0 0 12px" }}>
              Set this customer's own AI provider key (support). Validated on save, encrypted at rest, never shown again,
              scoped to this customer only. Removing it falls back to the KontractAI default.
            </p>
            {aiKeyErr && <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 6, backgroundColor: T.redS, color: T.red, fontSize: 12.5, border: `1px solid ${T.red}` }}>{aiKeyErr}</div>}
            {aiKeyMsg && <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 6, backgroundColor: T.grnS, color: T.grn, fontSize: 12.5, border: `1px solid ${T.grn}` }}>{aiKeyMsg}</div>}
            {BYO_AI_PROVIDERS.map(p => (
              <div key={p.id} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", padding: "8px 0", borderBottom: `1px solid ${T.brdSubtle}` }}>
                <span style={{ width: 84, fontSize: 13, fontWeight: 600, color: T.tx }}>{p.label}</span>
                {aiKeyProviders[p.id]
                  ? <><Badge c={T.grn} b={T.grnS}>Configured</Badge>
                      <span style={{ flex: 1, fontSize: 12, color: T.txM }}>Customer key set (never shown).</span>
                      <Btn v="ghost" sz="sm" disabled={aiKeyBusy === p.id} onClick={() => clearAiKey(p.id)}>{aiKeyBusy === p.id ? "…" : "Remove"}</Btn></>
                  : <><input type="password" autoComplete="off" value={aiKeyInput[p.id] || ""} onChange={e => setAiKeyInput(prev => ({ ...prev, [p.id]: e.target.value }))}
                        placeholder={p.id === "anthropic" ? "sk-ant-…" : p.id === "openai" ? "sk-…" : "gsk_…"}
                        style={{ flex: 1, minWidth: 200, padding: "7px 10px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 13, fontFamily: "monospace" }} />
                      <Btn v="primary" sz="sm" disabled={aiKeyBusy === p.id || !(aiKeyInput[p.id] || "").trim()} onClick={() => saveAiKey(p.id)}>{aiKeyBusy === p.id ? "Validating…" : "Save & validate"}</Btn></>}
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
              <Btn v="primary" onClick={() => setAiKeyTenant(null)}>Done</Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* ─── Global AI: catalog tier temperatures + platform default keys ─── */}
      <Modal open={defOpen} onClose={() => setDefOpen(false)} title="Provisioning Defaults · Bootstrap-sourced" w={980}>
        {defOpen && <DefaultsEditor tenants={tenants} />}
      </Modal>

      <Modal open={globalOpen} onClose={closeGlobal} title="Global AI · routing catalog + platform default keys" w={980}>
        {globalOpen && <GlobalAiConsole onRequestClose={closeGlobal} onDirtyChange={setGlobalDirty} />}
      </Modal>

      {/* ─── Operator per-tenant AI routing (Tier temperature overrides) ─── */}
      <Modal open={!!routingTenant} onClose={() => setRoutingTenant(null)} title={routingTenant ? `AI routing · ${routingTenant.name}` : ""} w={640}>
        {routingTenant && (
          <div>
            <p style={{ fontSize: 12, color: T.txM, margin: "0 0 12px" }}>
              Adjust this customer's per-tier <strong>temperature</strong>. Models stay governed by the global catalog —
              only temperature is per-customer here, and it applies whether the customer runs on their own AI key or the
              KontractAI default. Leave blank (Reset) to inherit the global value. Reasoning-model tiers reject temperature
              and are shown disabled.
            </p>
            {routingErr && <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 6, backgroundColor: T.redS, color: T.red, fontSize: 12.5, border: `1px solid ${T.red}` }}>{routingErr}</div>}
            {routingMsg && <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 6, backgroundColor: T.grnS, color: T.grn, fontSize: 12.5, border: `1px solid ${T.grn}` }}>{routingMsg}</div>}
            {routingTiers.length === 0 ? (
              <div style={{ textAlign: "center", color: T.txM, fontSize: 13, padding: 16 }}>Loading routing…</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {routingTiers.map((rt) => (
                  <div key={rt.tier} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", border: `1px solid ${T.brd}`, borderRadius: 8 }}>
                    <div style={{ width: 64, flexShrink: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: T.tx }}>Tier {rt.tier}</div>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, color: T.tx, fontFamily: "monospace" }}>{rt.model || "—"}</div>
                      <div style={{ fontSize: 11, color: T.txM }}>
                        global temp {rt.global_temperature != null ? rt.global_temperature : "—"}
                        {rt.override_temperature != null ? ` · override ${rt.override_temperature}` : " · no override"}
                        {` · effective ${rt.effective_temperature != null ? rt.effective_temperature : "omitted"}`}
                      </div>
                    </div>
                    {rt.supports_temperature ? (
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <input
                          type="number" step="0.05" min={0} max={1}
                          value={routingInput[rt.tier] ?? ""} onChange={e => setRoutingInput({ ...routingInput, [rt.tier]: e.target.value })}
                          placeholder={rt.global_temperature != null ? String(rt.global_temperature) : "0–1"}
                          style={{ width: 84, padding: "6px 8px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 13 }}
                        />
                        <Btn v="primary" sz="sm" disabled={routingBusy === rt.tier + "save"} onClick={() => saveRoutingTier(rt.tier)}>{routingBusy === rt.tier + "save" ? "…" : "Save"}</Btn>
                        <Btn v="ghost" sz="sm" disabled={routingBusy === rt.tier + "reset" || rt.override_temperature == null} onClick={() => resetRoutingTier(rt.tier)}>Reset</Btn>
                      </div>
                    ) : (
                      <div style={{ fontSize: 11.5, color: T.txD, fontStyle: "italic", maxWidth: 220, textAlign: "right" }}>
                        Temperature not supported by this tier's model (reasoning model) — not sent.
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
              <Btn v="primary" onClick={() => setRoutingTenant(null)}>Done</Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* ─── Operator-staged Microsoft Graph email (reusable credential pattern) ─── */}
      <Modal open={!!emailTenant} onClose={() => setEmailTenant(null)} title={emailTenant ? `Email (Microsoft Graph) · ${emailTenant.name}` : ""} w={560}>
        {emailTenant && (
          <div>
            <p style={{ fontSize: 12, color: T.txM, margin: "0 0 12px" }}>
              Stage this customer's Microsoft Graph email credentials on their behalf. The client secret is encrypted at
              rest and never shown again; only entered fields are updated. Clearing falls back to the KontractAI default.
            </p>
            {emailErr && <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 6, backgroundColor: T.redS, color: T.red, fontSize: 12.5, border: `1px solid ${T.red}` }}>{emailErr}</div>}
            {emailMsg && <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 6, backgroundColor: T.grnS, color: T.grn, fontSize: 12.5, border: `1px solid ${T.grn}` }}>{emailMsg}</div>}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {EMAIL_FIELD_ORDER.map((k) => {
                const f = emailFields ? emailFields[k] : null;
                const isSecret = k === "KONTRACTAI_EMAIL_CLIENT_SECRET";
                const label = (f && f.label) || k;
                return (
                  <div key={k} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    <label style={{ fontSize: 11.5, color: T.txM }}>
                      {label}{f && f.required ? " *" : ""}{" "}
                      {f && f.configured ? <Badge c={T.grn} b={T.grnS}>configured</Badge> : <span style={{ color: T.txM }}>· not set</span>}
                      {!isSecret && f && f.configured && f.value ? <span style={{ color: T.txM }}> · {String(f.value)}</span> : null}
                    </label>
                    <input
                      type={isSecret ? "password" : "text"} autoComplete="off"
                      value={emailInput[k] || ""} onChange={e => setEmailInput({ ...emailInput, [k]: e.target.value })}
                      placeholder={isSecret ? "•••••• (enter to replace)" : (f && f.value ? "enter to replace" : "")}
                      style={{ padding: "7px 10px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 13, fontFamily: isSecret ? "monospace" : "inherit" }}
                    />
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 18 }}>
              <Btn v="ghost" sz="sm" disabled={emailBusy} onClick={clearEmail}>Clear all</Btn>
              <div style={{ display: "flex", gap: 8 }}>
                <Btn v="primary" sz="sm" disabled={emailBusy} onClick={saveEmail}>{emailBusy ? "Saving…" : "Stage credentials"}</Btn>
                <Btn v="secondary" onClick={() => setEmailTenant(null)}>Done</Btn>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* ─── Operator-staged Harvey API key (reusable credential pattern) ─── */}
      <Modal open={!!harveyTenant} onClose={() => setHarveyTenant(null)} title={harveyTenant ? `Harvey AI · ${harveyTenant.name}` : ""} w={560}>
        {harveyTenant && (
          <div>
            <p style={{ fontSize: 12, color: T.txM, margin: "0 0 12px" }}>
              Stage this customer's Harvey API key on their behalf. The key is encrypted at rest and never shown again;
              clearing falls back to the KontractAI default.
            </p>
            {harveyErr && <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 6, backgroundColor: T.redS, color: T.red, fontSize: 12.5, border: `1px solid ${T.red}` }}>{harveyErr}</div>}
            {harveyMsg && <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 6, backgroundColor: T.grnS, color: T.grn, fontSize: 12.5, border: `1px solid ${T.grn}` }}>{harveyMsg}</div>}
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <label style={{ fontSize: 11.5, color: T.txM }}>
                Harvey API key *{" "}
                {harveyFields && harveyFields[HARVEY_KEY_FIELD]?.configured ? <Badge c={T.grn} b={T.grnS}>configured</Badge> : <span style={{ color: T.txM }}>· not set</span>}
              </label>
              <input
                type="password" autoComplete="off"
                value={harveyInput} onChange={e => setHarveyInput(e.target.value)}
                placeholder={harveyFields && harveyFields[HARVEY_KEY_FIELD]?.configured ? "•••••• (enter to replace)" : ""}
                style={{ padding: "7px 10px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 13, fontFamily: "monospace" }}
              />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 18 }}>
              <Btn v="ghost" sz="sm" disabled={harveyBusy} onClick={clearHarvey}>Clear</Btn>
              <div style={{ display: "flex", gap: 8 }}>
                <Btn v="primary" sz="sm" disabled={harveyBusy} onClick={saveHarvey}>{harveyBusy ? "Saving…" : "Stage key"}</Btn>
                <Btn v="secondary" onClick={() => setHarveyTenant(null)}>Done</Btn>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* ─── Operator-staged DocuSign credentials (shared config service door) ─── */}
      <Modal open={!!dsTenant} onClose={() => setDsTenant(null)} title={dsTenant ? `DocuSign e-signature · ${dsTenant.name}` : ""} w={620}>
        {dsTenant && (
          <div>
            <p style={{ fontSize: 12, color: T.txM, margin: "0 0 12px" }}>
              Stage this customer's DocuSign credentials on their behalf (their OWN DocuSign account — single designated
              user). Values are validated by the same shared service as the tenant settings page; secrets are encrypted at
              rest and never shown again. Staging reaches <em>Configured — not connected</em>: the one-time DocuSign
              consent must still be completed by a person with the designated user's DocuSign login.
            </p>
            {dsStatus && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, padding: "8px 12px", borderRadius: 6, backgroundColor: dsStatusBg(dsStatus.status), border: `1px solid ${dsStatusColor(dsStatus.status)}` }}>
                <span style={{ width: 9, height: 9, borderRadius: "50%", backgroundColor: dsStatusColor(dsStatus.status), display: "inline-block" }} />
                <span style={{ fontSize: 12.5, fontWeight: 600, color: dsStatusColor(dsStatus.status) }}>{dsStatus.status_label || dsStatus.status}</span>
                {dsStatus.connection_health === "degraded" && <Badge c={T.red} b={T.redS}>refresh failing — reconnect needed</Badge>}
                {dsStatus.enabled === false && <span style={{ fontSize: 11, color: T.txM }}>· sending disabled</span>}
                {dsStatus.refresh_last_ok_at && <span style={{ fontSize: 11, color: T.txM }}>· last refresh {new Date(dsStatus.refresh_last_ok_at).toLocaleString()}</span>}
              </div>
            )}
            {dsErr && <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 6, backgroundColor: T.redS, color: T.red, fontSize: 12.5, border: `1px solid ${T.red}` }}>{dsErr}</div>}
            {dsMsg && <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 6, backgroundColor: T.grnS, color: T.grn, fontSize: 12.5, border: `1px solid ${T.grn}` }}>{dsMsg}</div>}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {DS_FIELD_ORDER.map((k) => {
                const f = dsFields ? dsFields[k] : null;
                const isSecret = DS_SECRET_FIELDS.has(k);
                const label = (f && f.label) || k;
                return (
                  <div key={k} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    <label style={{ fontSize: 11.5, color: T.txM }}>
                      {label}{f && f.required ? " *" : ""}{" "}
                      {f && f.configured ? <Badge c={T.grn} b={T.grnS}>configured</Badge> : <span style={{ color: T.txM }}>· not set</span>}
                      {!isSecret && f && f.configured && f.value ? <span style={{ color: T.txM }}> · {String(f.value)}</span> : null}
                    </label>
                    <input
                      type={isSecret ? "password" : "text"} autoComplete="off"
                      value={dsInput[k] || ""} onChange={e => setDsInput({ ...dsInput, [k]: e.target.value })}
                      placeholder={isSecret ? "•••••• (enter to replace)" : (f && f.value ? "enter to replace" : "")}
                      style={{ padding: "7px 10px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 13, fontFamily: isSecret ? "monospace" : "inherit" }}
                    />
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 18 }}>
              <Btn v="ghost" sz="sm" disabled={dsBusy} onClick={clearDocusign}>Clear all</Btn>
              <div style={{ display: "flex", gap: 8 }}>
                <Btn v="primary" sz="sm" disabled={dsBusy} onClick={saveDocusign}>{dsBusy ? "Saving…" : "Stage credentials"}</Btn>
                <Btn v="secondary" onClick={() => setDsTenant(null)}>Done</Btn>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* ─── Operator cross-customer user management ─── */}
      <Modal open={!!manageTenant} onClose={() => setManageTenant(null)} title={manageTenant ? `Manage users · ${manageTenant.name}` : ""} w={720}>
        {manageTenant && (
          <div>
            <p style={{ fontSize: 12, color: T.txM, margin: "0 0 12px" }}>
              Operator-only · add, deactivate/reactivate, reset password, and delete this customer's users.
              Every action is audited. <em>Role changes and impersonation are out of scope.</em>
            </p>
            {/* Add user (WS-G): creates the user in this tenant + fires the set-password invite. */}
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 12, padding: "10px 12px", border: `1px dashed ${T.brd}`, borderRadius: 8 }}>
              <input value={addUser.name} onChange={e => setAddUser(p => ({ ...p, name: e.target.value }))} placeholder="Name (optional)"
                style={{ flex: 1, minWidth: 130, padding: "7px 9px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12.5 }} />
              <input value={addUser.email} onChange={e => setAddUser(p => ({ ...p, email: e.target.value }))} placeholder="user@customer.com" type="email"
                style={{ flex: 1.4, minWidth: 180, padding: "7px 9px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12.5 }} />
              <select value={addUser.role} onChange={e => setAddUser(p => ({ ...p, role: e.target.value }))}
                style={{ padding: "7px 9px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12.5 }}>
                {["requester", "lawyer", "legalops", "commercial_ops", "contract_manager", "business_reviewer", "executive", "admin"].map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <Btn v="primary" sz="sm" onClick={runAddUser} disabled={addUserBusy || !addUser.email.trim()}>{addUserBusy ? "Adding…" : "+ Add & invite"}</Btn>
            </div>
            {manageErr && <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 6, backgroundColor: T.redS, color: T.red, fontSize: 12.5, border: `1px solid ${T.red}` }}>{manageErr}</div>}
            {manageMsg && <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 6, backgroundColor: T.grnS, color: T.grn, fontSize: 12.5, border: `1px solid ${T.grn}` }}>{manageMsg}</div>}
            {manageLoading ? (
              <div style={{ textAlign: "center", color: T.txM, fontSize: 13, padding: 16 }}>Loading users…</div>
            ) : manageUsers.length === 0 ? (
              <div style={{ textAlign: "center", color: T.txM, fontSize: 13, padding: 16 }}>No users.</div>
            ) : (
              <div style={{ maxHeight: 400, overflow: "auto", border: `1px solid ${T.brd}`, borderRadius: 8 }}>
                {manageUsers.map((u: any) => (
                  <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderBottom: `1px solid ${T.brdSubtle}` }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: T.tx }}>{u.name || u.email}</div>
                      <div style={{ fontSize: 11, color: T.txM, fontFamily: "monospace" }}>{u.email}</div>
                    </div>
                    <span style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em" }}>{u.role}</span>
                    {u.is_active === false
                      ? <span style={{ fontSize: 10, color: T.txD, fontWeight: 600, padding: "2px 8px", borderRadius: 4, backgroundColor: T.bg4 }}>Deactivated</span>
                      : <span style={{ fontSize: 10, color: T.grn, fontWeight: 600, padding: "2px 8px", borderRadius: 4, backgroundColor: T.grnS }}>Active</span>}
                    <div style={{ display: "inline-flex", gap: 6 }}>
                      {u.is_active === false
                        ? <Btn v="success" sz="sm" onClick={() => userAction(u.id, "reactivate", `${u.email} reactivated.`)} disabled={rowBusy === u.id + "reactivate"}>Reactivate</Btn>
                        : <Btn v="secondary" sz="sm" onClick={() => userAction(u.id, "deactivate", `${u.email} deactivated.`)} disabled={rowBusy === u.id + "deactivate"}>Deactivate</Btn>}
                      <Btn v="ghost" sz="sm" onClick={() => userAction(u.id, "reset-invite", `Set-password link sent to ${u.email}.`)} disabled={rowBusy === u.id + "reset-invite"}>Reset password</Btn>
                      <Btn v="danger" sz="sm" onClick={() => { setDelUser(u); setDelUserText(""); }} disabled={rowBusy === u.id + "delete"}>Delete</Btn>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
              <Btn v="primary" onClick={() => setManageTenant(null)}>Done</Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* ─── Lifecycle confirm ─── */}
      {/* ─── WS-G: per-user permanent delete — type the exact email to confirm ─── */}
      <Modal open={!!delUser} onClose={() => { setDelUser(null); setDelUserText(""); }} title="Delete user permanently" w={480}>
        {delUser && (
          <div>
            <p style={{ fontSize: 13, color: T.tx, margin: "0 0 10px" }}>
              Permanently delete <strong>{delUser.email}</strong> from this customer? Their notifications, invites and
              access grants are removed with them. If they are owner-of-record on any data, the deletion is refused
              (nothing is ever orphaned) — deactivate instead.
            </p>
            <input value={delUserText} onChange={e => setDelUserText(e.target.value)} placeholder={`Type ${delUser.email} to confirm`}
              style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 13, fontFamily: "monospace", boxSizing: "border-box", marginBottom: 12 }} />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <Btn v="ghost" onClick={() => { setDelUser(null); setDelUserText(""); }}>Cancel</Btn>
              <Btn v="danger" onClick={runDeleteUser} disabled={delUserText !== delUser.email || rowBusy === delUser.id + "delete"}>Delete permanently</Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* ─── WS-G: permanent tenant deletion — type the org name + second confirm ─── */}
      <Modal open={!!delTenant} onClose={() => setDelTenant(null)} title="Delete tenant PERMANENTLY" w={540}>
        {delTenant && (
          <div>
            <div style={{ padding: "10px 12px", borderRadius: 6, backgroundColor: T.redS, color: T.red, fontSize: 12.5, border: `1px solid ${T.red}`, marginBottom: 12 }}>
              This is NOT offboarding. Every row this tenant owns — users, requests, contracts, versions, settings,
              provisioning artifacts — is deleted in one all-or-nothing transaction. <strong>Irreversible.</strong> Files already
              synced to the customer's SharePoint are retained there; the deletion itself is recorded in the operator audit.
            </div>
            {delErr && <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 6, backgroundColor: T.redS, color: T.red, fontSize: 12.5, border: `1px solid ${T.red}` }}>{delErr}</div>}
            <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: T.txD, marginBottom: 4, textTransform: "uppercase", letterSpacing: ".04em" }}>Type the organization name exactly</label>
            <input value={delName} onChange={e => setDelName(e.target.value)} placeholder={delTenant.name}
              style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${delName === delTenant.name ? T.grn : T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 13, boxSizing: "border-box", marginBottom: 10 }} />
            <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12.5, color: T.tx, cursor: "pointer", marginBottom: 14 }}>
              <input type="checkbox" checked={delSecond} onChange={e => setDelSecond(e.target.checked)} style={{ marginTop: 2 }} />
              I understand this permanently destroys all of {delTenant.name}'s data and cannot be undone.
            </label>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <Btn v="ghost" onClick={() => setDelTenant(null)} disabled={delBusy}>Cancel</Btn>
              <Btn v="danger" onClick={runDeleteTenant} disabled={delBusy || delName !== delTenant.name || !delSecond}>{delBusy ? "Deleting…" : "Delete tenant permanently"}</Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* ─── WS-F: platform-level health + backups (Dev Admin) ─── */}
      <Modal open={healthOpen} onClose={() => setHealthOpen(false)} title="Platform Health · whole dev platform" w={1000}>
        {healthOpen && (
          <div>
            <TabBar tabs={[{ id: "health", label: "Platform Health" }, { id: "backups", label: "Backups" }]} active={healthTab} onChange={setHealthTab} />
            <div style={{ marginTop: 12 }}>
              {healthTab === "health" ? <PlatformHealthPanel /> : <BackupStatus />}
            </div>
          </div>
        )}
      </Modal>

      {/* ─── WS-F: per-tenant health (tenant-scoped, wall-safe) ─── */}
      <Modal open={!!healthTenant} onClose={() => setHealthTenant(null)} title={healthTenant ? `Tenant health · ${healthTenant.name}` : ""} w={1000}>
        {healthTenant && <TenantHealthPanel tenantId={healthTenant.tenant_id} />}
      </Modal>

      <Modal open={!!confirmOp} onClose={() => setConfirmOp(null)} title={confirmOp ? `${confirmOp.verb} tenant` : ""} w={460}>
        {confirmOp && (
          <div>
            <p style={{ fontSize: 14, color: T.tx, margin: "0 0 8px" }}>
              {confirmOp.action === "suspend" && <>Suspend <strong>{confirmOp.tenant.name}</strong>? Its users will be blocked at sign-in (403 <code>tenant_inactive</code>) until reactivated.</>}
              {confirmOp.action === "reactivate" && <>Reactivate <strong>{confirmOp.tenant.name}</strong>? Its users will be able to sign in again. The admin password is not changed.</>}
              {confirmOp.action === "offboard" && <>Offboard <strong>{confirmOp.tenant.name}</strong>? This is a soft state (no data is deleted); users are blocked at sign-in. You can reactivate later.</>}
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 22 }}>
              <Btn v="ghost" onClick={() => setConfirmOp(null)} disabled={acting}>Cancel</Btn>
              <Btn v={confirmOp.action === "offboard" ? "danger" : "primary"} onClick={runLifecycle} disabled={acting}>
                {acting ? "Working…" : confirmOp.verb}
              </Btn>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={opsOpen} onClose={() => setOpsOpen(false)} title="Operators" w={620}>
        <div>
          <p style={{ fontSize: 12, color: T.txM, margin: "0 0 12px" }}>
            Operators are admins in the platform tenant. Adding an operator issues a set-password invite (no password is shown);
            the new operator signs in with password + 2FA. Every add/remove is reason-logged. The last active operator cannot be
            removed, and you cannot remove your own access.
          </p>
          {opsErr && <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 6, backgroundColor: T.redS, color: T.red, fontSize: 12.5, border: `1px solid ${T.red}` }}>{opsErr}</div>}
          {opsMsg && <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 6, backgroundColor: T.grnS, color: T.grn, fontSize: 12.5, border: `1px solid ${T.grn}` }}>{opsMsg}</div>}

          <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 14 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 3, flex: 1 }}>
              <label style={{ fontSize: 11.5, color: T.txM }}>Email</label>
              <input type="email" autoComplete="off" value={opAddEmail} onChange={(e) => setOpAddEmail(e.target.value)} style={inpStyle} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3, flex: 1 }}>
              <label style={{ fontSize: 11.5, color: T.txM }}>Name</label>
              <input type="text" autoComplete="off" value={opAddName} onChange={(e) => setOpAddName(e.target.value)} style={inpStyle} />
            </div>
            <Btn v="primary" sz="sm" disabled={opBusy} onClick={addOperator}>{opBusy ? "Working…" : "Add operator"}</Btn>
          </div>

          {opsLoading ? (
            <div style={{ fontSize: 13, color: T.txM, textAlign: "center", padding: 16 }}>Loading operators…</div>
          ) : operators.length === 0 ? (
            <div style={{ fontSize: 13, color: T.txM, textAlign: "center", padding: 16 }}>No operators found.</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={th}>Operator</th><th style={th}>Status</th><th style={{ ...th, textAlign: "right" }}>Action</th></tr></thead>
              <tbody>
                {operators.map((op) => (
                  <tr key={op.id}>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{op.name || op.email}</div>
                      <div style={{ fontSize: 11.5, color: T.txM }}>{op.email}</div>
                    </td>
                    <td style={td}>{op.is_active ? <Badge c={T.grn} b={T.grnS}>Active</Badge> : <Badge c={T.txD} b={T.bg4}>Inactive</Badge>}</td>
                    <td style={{ ...td, textAlign: "right" }}>
                      {op.is_active && <Btn v="danger" sz="sm" disabled={opBusy} onClick={() => removeOperator(op)}>Remove</Btn>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
            <Btn v="secondary" onClick={() => setOpsOpen(false)}>Done</Btn>
          </div>
        </div>
      </Modal>

      <Modal open={ssoOpen} onClose={() => setSsoOpen(false)} title="Operator SSO · Microsoft Entra (OIDC)" w={560}>
        <div>
          <p style={{ fontSize: 12, color: T.txM, margin: "0 0 12px" }}>
            Configure operator sign-in via Microsoft Entra. Safe-by-construction: this provider is always mapped to the
            platform tenant with <strong>auto-provisioning OFF</strong> (an unrecognized Entra user is refused — no account,
            no operator) and a non-admin default role. Password + 2FA stays available as the fallback. The client secret is
            encrypted at rest and never shown again.
          </p>
          {ssoErr && <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 6, backgroundColor: T.redS, color: T.red, fontSize: 12.5, border: `1px solid ${T.red}` }}>{ssoErr}</div>}
          {ssoMsg && <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 6, backgroundColor: T.grnS, color: T.grn, fontSize: 12.5, border: `1px solid ${T.grn}` }}>{ssoMsg}</div>}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <label style={{ fontSize: 11.5, color: T.txM }}>Application (client) ID</label>
              <input type="text" autoComplete="off" value={ssoInput.client_id} onChange={(e) => setSsoInput({ ...ssoInput, client_id: e.target.value })} style={inpStyle} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <label style={{ fontSize: 11.5, color: T.txM }}>Directory (tenant) ID — the Entra <code>tid</code></label>
              <input type="text" autoComplete="off" value={ssoInput.tenant_id_external} onChange={(e) => setSsoInput({ ...ssoInput, tenant_id_external: e.target.value })} style={inpStyle} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <label style={{ fontSize: 11.5, color: T.txM }}>
                Client secret {ssoConfig?.has_secret ? <Badge c={T.grn} b={T.grnS}>configured</Badge> : <span style={{ color: T.txM }}>· not set</span>}
              </label>
              <input type="password" autoComplete="off" value={ssoInput.client_secret} onChange={(e) => setSsoInput({ ...ssoInput, client_secret: e.target.value })} placeholder={ssoConfig?.has_secret ? "•••••• (enter to replace)" : ""} style={{ ...inpStyle, fontFamily: "monospace" }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <label style={{ fontSize: 11.5, color: T.txM }}>Allowed email domains (comma-separated)</label>
              <input type="text" autoComplete="off" value={ssoInput.allowed_email_domains} onChange={(e) => setSsoInput({ ...ssoInput, allowed_email_domains: e.target.value })} style={inpStyle} />
            </div>
            <label style={{ fontSize: 12.5, color: T.tx, display: "flex", alignItems: "center", gap: 8 }}>
              <input type="checkbox" checked={ssoInput.is_active} onChange={(e) => setSsoInput({ ...ssoInput, is_active: e.target.checked })} /> Active (operators may sign in via Microsoft)
            </label>
            <div style={{ fontSize: 11.5, color: T.txM }}>
              Test the round-trip without creating a session: <a href="/api/auth/microsoft?test_mode=true" style={{ color: T.acc }}>run SSO test sign-in</a>.
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 18 }}>
            <Btn v="ghost" sz="sm" disabled={ssoBusy || !ssoConfig} onClick={deactivateSso}>Deactivate</Btn>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn v="primary" sz="sm" disabled={ssoBusy} onClick={saveSso}>{ssoBusy ? "Saving…" : "Save"}</Btn>
              <Btn v="secondary" onClick={() => setSsoOpen(false)}>Done</Btn>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};
